import React from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { IFirearm } from 'models/firearm';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { IDictionary } from 'models/dictionary';
import { LegalityTypes } from 'constants/LegalityTypes';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';

interface IFirearmDataListItem {
  data: IFirearm;
  state: IDictionary[] | null;
  make: IDictionary[] | null;
  model: IDictionary[] | null;
}

const FirearmDataListItem = ({ data, state, make, model }: IFirearmDataListItem) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { hasPermissions } = usePermission();
  const isIllegal = data.legalityUniqueId === LegalityTypes.ILLEGAL;

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: `${isIllegal ? colors.error15 : colors.gray100}`,
      }}
    >
      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(
            'registeredFirearms.firearmsDetails.detailedInformation.section.serialNumber.label',
          )}
        </Typography>
        <Typography variant="body2">{data.serialNumber}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(
            'registeredFirearms.firearmsDetails.detailedInformation.section.firecycleID.label',
          )}
        </Typography>
        <Typography variant="body2">{data.fireCycleId}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.make.column')}
        </Typography>
        <Typography variant="body2">{findInDictionary(data.makeUniqueId, make)}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.model.column')}
        </Typography>
        <Typography variant="body2">{findInDictionary(data.modelUniqueId, model)}</Typography>
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.state.column')}
        </Typography>
        <Chips
          text={findInDictionary(data.stateUniqueId, state)}
          variant={
            renderDataColorByValues(
              data.activityUniqueId,
              data.legalityUniqueId,
              data.stateUniqueId,
            ).stateField
          }
        />
      </Grid>

      {hasPermissions([EPermission.VIEW_FIREARMS, EPermission.VIEW_ALL_FIREARMS], true) && (
        <Grid
          size={{ xs: 12 }}
          sx={{
            borderTop: `1px solid ${colors.gray200}`,
            p: 2,
            margin: '0 10px',
          }}
        >
          <Button
            label={t('registeredFirearms.firearmsLocation.seeDetails.label')}
            variant={EButtonVariants.outlined}
            onClick={() =>
              navigate(`${ERouteLinks.ViewRegisteredFirearm.replace(':id', data.uniqueId)}`)
            }
            size="small"
            fullWidth
          />
        </Grid>
      )}
    </Grid>
  );
};

export default FirearmDataListItem;
